import calculateLongTermStorage from "./calculations/calculateLongTermStorage";
import calculateAvailability from "./calculations/calculateAvailability";
import calculateShortTermStorage from "./calculations/calculateShortTermStorage";
import calculateReserves from "./calculations/calculateReserves";
import calculateCurtailment from "./calculations/calculateCurtailment";
import calculateAverageGeneration from "./calculations/calculateAverageGeneration";
import calculateAnnualCosts from "./calculations/CalculateAnnualCosts";
import calculateAnnualDemand from "./calculations/calculateAnnualDemand";
import calculateAverageDemand from "./calculations/calculateAverageDemand";
import calculateTotalAnnualCost from "./calculations/calculateTotalAnnualCost";
import calculateCarbonPerTechnology from "./calculations/calculateCarbonPerTechnology";
import calculateTotalAnnualCarbon from "./calculations/calculateTotalAnnualCarbon";
import calculateCarbonCostPercentages from "./calculations/calculateCarbonCostPercentages";
import calculateGenerationTable from "./calculations/calculateGenerationTable";
import calculateAnnualGeneration from "./calculations/calculateAnnualGeneration";
import calculateSimpleAverageCharts from "./calculations/calculateSimpleAverageCharts";
import firstRunOperations from "./calculations/firstRunOperations";
import dataIndexFn from "./objects/dataIndexFn";
import calcOptionsFn from "./objects/calcOptionsFn";
import annualConstFn from "./objects/annualConstFn";

import { zeroArrayTemplate, dayNumberInData } from "./functions";
import { round, min } from "mathjs";
import calculateAnnualCurtailment from "./calculations/calculateAnnualCurtailment";
import type {
  AnnualConst,
  CalculatedData,
  FirstRunValues,
  InitialData,
  NumberMatrix,
  NumberMatrix3D,
} from "./algorithm.types";

export type RunCalculationParams = {
  initialData: InitialData;
  firstRunValues: FirstRunValues;
  sliderValues: Array<number>;
};

function runCalculation({
  initialData,
  firstRunValues,
  sliderValues,
}: RunCalculationParams): CalculatedData {
  const { daySettingsData, allDayData, optionsData, technologyData } =
    initialData;

  const annualConst: AnnualConst = annualConstFn(optionsData);
  const dataIndex = dataIndexFn(optionsData);
  const calcOptions = calcOptionsFn({ optionsData, allDayData });
  const { noOfTimeSlots, noOfTechs, noOfDayTypes, noOfDaysPerSeason } =
    calcOptions;

  const dayChartData: NumberMatrix3D = [];

  const demandCalcData: NumberMatrix = zeroArrayTemplate(
    noOfTimeSlots,
    noOfDayTypes,
  );
  const averageGenPerDay: NumberMatrix = zeroArrayTemplate(
    noOfTechs,
    noOfDayTypes,
  );
  const reservesAvali: NumberMatrix = [];

  for (let i = 0; i < noOfDayTypes; i++) {
    demandCalcData[i] = [...daySettingsData[i][0]];
  }
  annualConst.annualTechGen = Array(noOfDayTypes).fill(0);

  const genPowerResults: Array<number> = Array(noOfDayTypes).fill(0);

  if (sliderValues[dataIndex.longTermStorage] > 0) {
    calculateLongTermStorage({
      calcOptions,
      daySettingsData,
      dataIndex,
      technologyData,
      genPowerResults,
      demandCalcData,
      sliderValues,
    });
  }

  for (let season = 1; season <= calcOptions.noOfSeasons; season++) {
    for (
      let dayOfTheSeason = 1;
      dayOfTheSeason <= noOfDaysPerSeason;
      dayOfTheSeason++
    ) {
      calcOptions.season = season;
      calcOptions.dayOfTheSeason = dayOfTheSeason;

      const availData = calculateAvailability({
        calcOptions,
        daySettingsData,
        sliderValues,
      }); // SLIDERS x TIME FACTORS

      let genData = calculateGenerationTable({
        calcOptions,
        availData,
        demandCalcData,
        dataIndex,
        genPowerResults,
      }); // CALC GENERATION FOR EACH TECH

      if (sliderValues[dataIndex.shortTermStorage] > 0) {
        genData = calculateShortTermStorage({
          sliderValues,
          calcOptions,
          dataIndex,
          technologyData,
          availData,
          genData,
          averageGenPerDay,
        });
      } // BATTERY STORAGE PROGRAM

      reservesAvali.push(
        round(
          calculateReserves({
            daySettingsData,
            calcOptions,
            dataIndex,
            availData,
          }),
          2,
        ) as Array<number>,
      ); // calculate RESERVES

      const curtailment = calculateCurtailment({
        calcOptions,
        technologyData,
        availData,
        genData,
      }); // UNUSED CARBON ZERO GENERATION

      annualConst.averageGenPerDay = calculateAverageGeneration({
        calcOptions,
        genData,
        averageGenPerDay,
      }); // DAY AVERAGE GENERATION PER DAY

      annualConst.averageDemandPerDay = calculateAverageDemand({
        calcOptions,
        daySettingsData,
        annualConst,
      }); // DEMAND AVERAGE GENERATION PER DAY

      genData.push(round(curtailment, 2) as Array<number>);
      genData.push(
        daySettingsData[
          dayNumberInData(season, dayOfTheSeason, noOfDaysPerSeason)
        ][0],
      );
      dayChartData.push(round(genData, 2) as NumberMatrix);
    } // dayOfTheSeason loop
  } // season loop

  annualConst.annualTechGen = calculateAnnualGeneration({
    calcOptions,
    annualConst,
  }); // ANNUAL GENERATION INC DAY PERCENTAGE
  annualConst.annualTechCost = calculateAnnualCosts({
    technologyData,
    calcOptions,
    annualConst,
    sliderValues,
  }); // ANNUAL TECH RUNNING COSTS
  annualConst.curtailment = calculateAnnualCurtailment({
    calcOptions,
    dayChartData,
  }); // ANNUAL GENERATION INC DAY PERCENTAGE

  const annualDemand = calculateAnnualDemand({ calcOptions, annualConst });
  annualConst.annualDemand = annualDemand; // ANNUAL DEMAND INC DAY PERCENTAGE

  const totalAnnualCost = calculateTotalAnnualCost({
    annualConst: { ...annualConst, annualDemand },
  });
  annualConst.totalAnnualCost = totalAnnualCost; // ANNUAL EACH TECH TOTAL COST

  annualConst.annualTechCarbon = calculateCarbonPerTechnology({
    annualConst,
    technologyData,
  }); // ANNUAL CARBON PER TECHNOLOGY

  const totalAnnualCarbon = calculateTotalAnnualCarbon({
    dataIndex,
    calcOptions,
    annualConst: { ...annualConst, annualDemand },
  });
  annualConst.totalAnnualCarbon = totalAnnualCarbon; // ANNUAL ALL TECH CARBON TOTAL

  // STORE FIRST RUN RESULTS FOR COMPARISON
  if (firstRunValues.firstRun) {
    firstRunValues = firstRunOperations({
      annualConst: { ...annualConst, annualDemand },
      sliderValues,
      reservesAvali,
      initialData,
    });
  }

  const carbonCost = calculateCarbonCostPercentages({
    annualConst: { ...annualConst, totalAnnualCost, totalAnnualCarbon },
    firstRunValues: {
      ...firstRunValues,
      totalAnnualCostOriginal: firstRunValues.totalAnnualCostOriginal as number,
      totalAnnualCarbonOriginal:
        firstRunValues.totalAnnualCarbonOriginal as number,
    },
  });
  Object.assign(annualConst, carbonCost);

  const simpleDataCharts = calculateSimpleAverageCharts(dayChartData);
  dayChartData.push(
    round(simpleDataCharts.nonDroughtAverageData, 2) as NumberMatrix,
  );
  dayChartData.push(
    round(simpleDataCharts.droughtAverageData, 2) as NumberMatrix,
  );

  const reserveRemaining = round(min(reservesAvali) * 100, 1);

  const calculatedData: CalculatedData = {
    simpleDataCharts,
    dayChartData,
    annualConst,
    reserveRemaining,
    reservesAvali,
    firstRunValues,
    dataIndex,
  };

  return calculatedData;
}
export default runCalculation;
